/* eslint-disable no-barrel-files/no-barrel-files */
import { fg } from '@atlaskit/platform-feature-flags';
import { type ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import {
	type ConfluenceRangeFilter,
	type ThirdPartyFilters,
	type ThirdPartyProductFilter,
} from '@atlassian/search-client/query-generator';

import {
	OpportunityStatusFilterValue,
	type OpportunityStatusFilterValueKey,
} from '../../constants/filters/opportunity-status-filter';
import { NounSubtypeKey, TypeFilterValueKey } from '../../constants/filters/universal-type';
import { GITHUB_PRODUCT_KEYS, is3pProductKey, ProductKeys } from '../../constants/product-keys';
import {
	type ConnectorSource,
	FEDERATED_CONNECTOR_SOURCE,
	FULL_CONNECTOR_SOURCE,
	isForgeConnectorProductKey,
	LITE_CONNECTOR_SOURCE,
	LITE_CONNECTOR_SOURCE_PREFIX,
	SMARTLINKS_CONNECTOR_SOURCE,
	SMARTLINKS_RESOURCE_TYPE_PREFIX,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../constants/schemas/3p-config';
import { ProductKey } from '../../constants/schemas/product-key';
import type { SelectedFiltersType } from '../../constants/types';
import {
	removeFedSlackFromAllProductSearch,
	serveWeakLinkExpWithoutExposure,
} from '../experiments';

import { getProducts3PFromConfigForFilter as _getProducts3PFromConfigForFilter } from './get-products-3p-from-config';
// eslint-disable-next-line import/order
import { baseIntegrationARIs, type IntegrationMap } from './integration-aris';
// eslint-disable-next-line import/order
import type { FilterId } from '../../constants/filters';

const DEFAULT_ENTITY_SUBTYPES: Partial<Record<TypeFilterValueKey, NounSubtypeKey[]>> = {
	[TypeFilterValueKey.Document]: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
	[TypeFilterValueKey.Spreadsheet]: [NounSubtypeKey.Spreadsheet],
	[TypeFilterValueKey.Presentation]: [NounSubtypeKey.Presentation],
	[TypeFilterValueKey.Video]: [NounSubtypeKey.Video],
	[TypeFilterValueKey.Design]: [NounSubtypeKey.Design],
	[TypeFilterValueKey.Conversation]: [],
	[TypeFilterValueKey.Message]: [],
	// Comment filter should map to the comment subtype to avoid returning unrelated results
	// (e.g. web pages) for products that don't have an explicit typeFilterConfig for comments
	[TypeFilterValueKey.Comment]: [NounSubtypeKey.Comment],
	[TypeFilterValueKey.Image]: [NounSubtypeKey.Image],
	[TypeFilterValueKey.Audio]: [NounSubtypeKey.Audio],
	[TypeFilterValueKey.Pdf]: [NounSubtypeKey.Pdf],
	[TypeFilterValueKey.Code]: [NounSubtypeKey.Code],
	[TypeFilterValueKey.Archive]: [NounSubtypeKey.Archive],
	[TypeFilterValueKey.Folder]: [NounSubtypeKey.Folder],
	[TypeFilterValueKey.Shortcut]: [NounSubtypeKey.Shortcut],
	[TypeFilterValueKey.Form]: [NounSubtypeKey.Form],
	[TypeFilterValueKey.Notebook]: [NounSubtypeKey.Notebook],
	[TypeFilterValueKey.Other]: [NounSubtypeKey.Other],
	[TypeFilterValueKey.WebPage]: [NounSubtypeKey.WebPage],
	[TypeFilterValueKey.Dashboard]: [NounSubtypeKey.Dashboard],
	[TypeFilterValueKey.Database]: [NounSubtypeKey.Database],
};

export const getEmptySelectedFilters: () => SelectedFiltersType = (): SelectedFiltersType => ({
	commonFilters: {},
	confluenceFilters: {
		spacesFilter: [],
		contributorsFilter: [],
		ancestorIdsFilter: [],
		range: [],
		owners: [],
		labelsFilter: [],
		titleMatchOnly: false,
		contentStatuses: [],
		containerStatus: [],
	},
	assetsFilters: {
		objectSchemaFilter: {
			schemaARIs: [],
		},
		objectTypeFilter: {
			typeARIs: [],
		},
	},
	jiraFilters: {
		issueFilter: {
			assigneeARIs: [],
			reporterARIs: [],
			commenterARIs: [],
			projectARIs: [],
			issueTypeIDs: [],
			issueLabels: [],
			issueTypes: [],
			statusCategories: [],
			watcherARIs: [],
		},
	},
	mercuryFilters: {
		owners: [],
		focusAreaTypeIds: [],
		ancestorIds: [],
	},
	talentFilters: {
		owners: [],
		employmentTypes: [],
		focusAreas: [],
		jobFamilies: [],
		statuses: [],
		teams: [],
	},
	compassFilters: {
		componentFilter: {
			componentStates: [],
		},
	},
	thirdPartyFilters: {
		titleMatchOnly: false,
		subtypes: [],
		integrations: [],
		createdBy: [],
		lastUpdatedBy: [],
		assignees: [],
		labels: [],
	},
	sortFilters: [],
});

// Re-exported from date-sort-utils.ts (source of truth for pure date/sort functions)
// TODO: update call sites to import from date-sort-utils directly, then remove this re-export
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { convertCustomDateToRange } from './convert-custom-date-to-range';
export { getUTCDateRange } from './get-utc-date-range';
export {
	convertLastUpdatedToDateRange,
	getConfluenceDateOptionFromCustomDates,
	getConfluenceDateOptionValues,
} from './date-sort-utils';
export { convertSortToFilter } from './convert-sort-to-filter';

const DATE_RANGE_PRESETS = [
	{ name: 'today', fromOffset: 0, toOffset: 0 },
	{ name: 'yesterday', fromOffset: 1, toOffset: 1 },
	{ name: 'past7Days', fromOffset: 7, toOffset: 0 },
	{ name: 'past30Days', fromOffset: 30, toOffset: 0 },
	{ name: 'pastYear', fromOffset: 365, toOffset: 0 },
] as const;

/**
 * Gets a UTC date string (YYYY-MM-DD) for a given number of days ago.
 * Uses UTC timezone to match backend date calculations.
 */
const getUTCDateString = (daysAgo: number): string => {
	const now = new Date();
	const utcDate = new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - daysAgo),
	);
	return utcDate.toISOString().split('T')[0];
};

/**
 * Converts a date range object back to a lastUpdated preset string.
 *
 * Assumption: Dates are in UTC timezone
 */
export const convertDateRangeToLastUpdated: (
	dateRange:
		| {
				gt?: string | null;
				lt?: string | null;
		  }
		| undefined
		| null,
) => 'custom' | 'anytime' | 'today' | 'yesterday' | 'past7Days' | 'past30Days' | 'pastYear' = (
	dateRange: { gt?: string | null; lt?: string | null } | undefined | null,
) => {
	if (!dateRange || (!dateRange.gt && !dateRange.lt)) {
		return 'anytime';
	}

	if (!dateRange.gt) {
		return 'custom';
	}

	const inputGtDate = dateRange.gt.split('T')[0];
	const inputLtDate = dateRange.lt?.split('T')[0];

	for (const preset of DATE_RANGE_PRESETS) {
		const presetGtDate = getUTCDateString(preset.fromOffset);
		const presetLtDate = getUTCDateString(preset.toOffset);

		if (inputGtDate === presetGtDate) {
			// If BE has also provided lt, it must match the preset's lt for this to be the preset.
			// E.g. gt='2026-02-01', lt='2026-02-03' is a custom range (Feb 1–3), not past7Days
			if (inputLtDate != null && inputLtDate !== presetLtDate) {
				continue;
			}
			return preset.name;
		}
	}

	return 'custom';
};

// Permissive input types to support Relay-generated types without casting
type ParticipantInput = {
	combination: string;
	type: string;
	users: ReadonlyArray<string>;
};

type ParticipantsInput = {
	combination: string;
	items: ReadonlyArray<ParticipantInput>;
};

const isSupportedParticipant = (participant: ParticipantInput): boolean => {
	const { users, combination, type } = participant;

	if (type !== 'PRESENCE') {
		return false;
	}

	if (users.length > 1 && combination === 'AND') {
		return false;
	}

	return true;
};

export const getContributorsFromParticipants: (participants: ParticipantsInput) => string[] = (
	participants: ParticipantsInput,
): string[] => {
	const { items, combination } = participants;

	if (items.length > 1 && combination === 'AND') {
		return [];
	}

	if (!items.every(isSupportedParticipant)) {
		return [];
	}

	return Array.from(new Set(items.flatMap(({ users }) => users)));
};

/**
 * Returns integration ARIs from the static mapping, filtered to only
 * include products that are enabled in the provided bootstrap configs.
 *
 * Previously iterated ThirdPartyConfigs (pulling in icon-laden 3P config files).
 * Now uses the pre-computed baseIntegrationARIs with runtime filtering.
 *
 * Note: Dynamic Forge connector ARIs are handled separately in getThirdPartyFilters()
 * via the thirdPartyConfigs bootstrap parameter.
 */
export const getIntegrationARIsFromConfig: () => IntegrationMap = (): IntegrationMap => {
	return { ...baseIntegrationARIs };
};

/**
 * Checks the product-specific feature gate for video noun filtering.
 * - Google Drive: controlled by 'rovo_fps_enable_video_noun_filter'
 * - SharePoint: controlled by 'rovo_fps_sharepoint_enable_video_noun_filter'
 * - Other products: returns false (not supported)
 */
const isVideoNounFilterEnabled = (product: ProductKeys): boolean => {
	if (product === ProductKey.GoogleDrive) {
		return fg('rovo_fps_enable_video_noun_filter');
	}
	if (product === ProductKey.Sharepoint) {
		return fg('rovo_fps_sharepoint_enable_video_noun_filter');
	}
	return false;
};

const buildGitFilters = (
	pullRequestStatus?: string[],
	repoName?: string[],
	reviewer?: string[],
): Pick<ThirdPartyFilters, 'gitFilters'> | undefined => {
	if (!pullRequestStatus?.length && !repoName?.length && !reviewer?.length) {
		return undefined;
	}
	return {
		gitFilters: {
			...(pullRequestStatus?.length && { pullRequestStatus }),
			...(repoName?.length && { repoName }),
			...(reviewer?.length && { reviewer }),
		},
	};
};

/**
 * Build MS Teams (`teamsFilters`) wire payload.
 * - `hasAttachment` (boolean): scopes Teams results to messages with attachments.
 * - `workspaceName` (string[]): `teamsFilters.workspaceName` — filters by workspace (team) name.
 *   Gated behind `rovo_fps_show_workspace_name_filter` on the frontend and
 *   `xpsearch-aggregator_teams_workspace_name_filter` on the backend.
 * Returns `undefined` when no Teams filter is set so the caller can spread without sending an empty object.
 */
const buildTeamsFilters = (
	hasAttachment?: boolean,
	workspaceName?: string[],
): Pick<ThirdPartyFilters, 'teamsFilters'> | undefined => {
	if (!hasAttachment && !workspaceName?.length) {
		return undefined;
	}
	return {
		teamsFilters: {
			...(hasAttachment && { hasAttachment: true }),
			...(workspaceName?.length && { workspaceName }),
		},
	};
};

const filterConnectorSources = (
	product: ProductKeys,
	isAllProductSearch: boolean,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	connectorSources: ConnectorSource[] | undefined,
): ConnectorSource[] | undefined => {
	if (
		product === ProductKey.Slack &&
		removeFedSlackFromAllProductSearch(thirdPartyConfigs, isAllProductSearch)
	) {
		// When a federated Slack connector exists in all-product search,
		// exclude both 'federated' and 'smartlinks' connector sources so that
		// only the federated connector serves results in that context.
		return connectorSources?.filter(
			(s) =>
				s !== FEDERATED_CONNECTOR_SOURCE &&
				(!fg('ai_3p_slack_smartlinks_federated_connector_enabled') ||
					s !== SMARTLINKS_CONNECTOR_SOURCE),
		);
	}

	return connectorSources;
};

const hasConnectorSources = (
	product: ProductKeys,
	isAllProductSearch: boolean,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): boolean => {
	const connectorSources = thirdPartyConfigs[product as ProductKeys3P]?.connectorSources;
	const filteredConnectorSources = filterConnectorSources(
		product,
		isAllProductSearch,
		thirdPartyConfigs,
		connectorSources,
	);
	return (filteredConnectorSources?.length ?? 0) > 0;
};

export const getThirdPartyFilters: (
	products: ProductKeys[],
	titleMatchValue: boolean,
	entities: TypeFilterValueKey[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isAllProductSearch?: boolean,
	range?: ConfluenceRangeFilter[],
	assignees?: string[],
	containerAris?: string[],
	createdBy?: string[],
	lastUpdatedBy?: string[],
	ancestorAris?: string[],
	labels?: string[],
	additionalTexts?: string[],
	selectedDatasource?: string,
	salesforceOwners?: string[],
	salesforceStage?: string[],
	salesforceCloseDate?: string,
	opportunityStatus?: string,
	pullRequestStatus?: string[],
	repoName?: string[],
	campaignStatuses?: string[],
	caseStatuses?: string[],
	slackChannels?: string[],
	hasAttachment?: boolean,
	workspaceName?: string[],
	reviewer?: string[],
) => ThirdPartyFilters = (
	products: ProductKeys[],
	titleMatchValue: boolean,
	// entities is the frontend filtered, already selected values
	entities: TypeFilterValueKey[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isAllProductSearch: boolean = true,
	range?: ConfluenceRangeFilter[],
	assignees?: string[],
	containerAris?: string[],
	createdBy?: string[],
	lastUpdatedBy?: string[],
	ancestorAris?: string[],
	labels?: string[],
	additionalTexts?: string[],
	selectedDatasource?: string,
	salesforceOwners?: string[],
	salesforceStage?: string[],
	salesforceCloseDate?: string,
	opportunityStatus?: string,
	pullRequestStatus?: string[],
	repoName?: string[],
	campaignStatuses?: string[],
	caseStatuses?: string[],
	slackChannels?: string[],
	hasAttachment?: boolean,
	workspaceName?: string[],
	reviewer?: string[],
): ThirdPartyFilters => {
	if (
		!products.length ||
		products.some(
			(p) => is3pProductKey(p) || isForgeConnectorProductKey(p) || p === ProductKey.Bitbucket,
		)
	) {
		// Conditionally create the dynamic integration ARIs based on the config
		const integrationARIsFromConfig = getIntegrationARIsFromConfig();

		// Combine static and dynamic integration ARIs
		let productToIntegrationARIMap: IntegrationMap = integrationARIsFromConfig;

		// Add Forge connector integration ARIs from thirdPartyConfigs
		products.forEach((product) => {
			if (isForgeConnectorProductKey(product) && thirdPartyConfigs[product]) {
				const integrationAri = thirdPartyConfigs[product]?.integrationAri;
				productToIntegrationARIMap[product] = integrationAri;
			}
		});

		// Note: Bitbucket integration ARI is already included in baseIntegrationARIs

		// get the subtypes by looking at each type's subcategories
		let allSubtypes: NounSubtypeKey[] = [];
		let allContainerTypes: string[] = [];

		let thirdPartyProducts: ThirdPartyProductFilter[] = [];

		const filteredProducts = products
			.filter(
				(product) =>
					(is3pProductKey(product) || isForgeConnectorProductKey(product)) &&
					thirdPartyConfigs[product] !== undefined &&
					(!thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth ||
						isImpersonationEnabled(product, thirdPartyConfigs)) &&
					!thirdPartyConfigs[product as ProductKeys3P]?.isFederated &&
					hasConnectorSources(product, isAllProductSearch, thirdPartyConfigs),
			)
			.filter((product) => {
				return thirdPartyConfigs[product as ProductKeys3P]?.isEnabled?.();
			});

		// Products that support embedded comments (comments are returned as linkedEntities within documents)
		const productsWithEmbeddedComments: ProductKeys[] = [
			ProductKey.GoogleDrive,
			ProductKey.Sharepoint,
		];

		// Skip code search (Document entity) unless GitHub is the only selected product. Aligned
		// with `includeGithubCode` in services/search-query-generator/index.ts so the entities payload
		// and the Relay `@include(if: $includeGithubCode)` directive can't diverge.
		const isOnlyGithubSelected = products.length === 1 && GITHUB_PRODUCT_KEYS.includes(products[0]);

		for (let product of filteredProducts) {
			const thirdPartyConfig = thirdPartyConfigs[product as ProductKeys3P];
			if (!thirdPartyConfig) {
				continue;
			}

			const isGithubProduct = GITHUB_PRODUCT_KEYS.includes(product);
			const shouldSkipDocumentForGithub = isGithubProduct && !isOnlyGithubSelected;

			let localSubtypes: NounSubtypeKey[] = [];
			let productEntities: string[] = [];
			let connectionIds: string[] | undefined;
			let linkedEntityGranularity: string | undefined;

			// Check if Comment filter is selected for a product that supports embedded comments.
			// Each product is gated behind its own feature flag so support can be toggled
			// independently:
			//   - Google Drive: enable_3p_embedded_comments_search
			//   - SharePoint:   rovo_fps_sharepoint_embedded_comments
			const isCommentFilterSelected = entities.includes(TypeFilterValueKey.Comment);
			const isEmbeddedCommentsEnabledForProduct =
				(product === ProductKey.GoogleDrive && fg('enable_3p_embedded_comments_search')) ||
				(product === ProductKey.Sharepoint && fg('rovo_fps_sharepoint_embedded_comments'));
			const supportsEmbeddedComments = productsWithEmbeddedComments.includes(
				product as ProductKeys,
			);

			if (
				isCommentFilterSelected &&
				supportsEmbeddedComments &&
				isEmbeddedCommentsEnabledForProduct
			) {
				// Use ONLY_LINKED_ENTITIES to force match only on comments.
				// Note: linkedEntities input is deprecated and ignored by the aggregator —
				// linkedEntityGranularity alone controls this behaviour.
				linkedEntityGranularity = 'ONLY_LINKED_ENTITIES';
			}

			// Check if the product has specific typeFilterConfig for the requested entities
			let hasMatchingTypeConfig = false;
			thirdPartyConfig.typeFilterConfig?.forEach((entitiesConfig, entityValue) => {
				if (entities.includes(entityValue)) {
					localSubtypes = [...localSubtypes, ...entitiesConfig.subTypes3P];
					hasMatchingTypeConfig = true;
				}
			});

			if (fg('rovo-search-dynamic-forge-config')) {
				// Extract all entities from typeFilterConfig for this product
				if (thirdPartyConfig.typeFilterConfig) {
					thirdPartyConfig.typeFilterConfig.forEach((entitiesConfig, entityValue) => {
						if (
							isCommentFilterSelected &&
							supportsEmbeddedComments &&
							isEmbeddedCommentsEnabledForProduct &&
							entityValue === TypeFilterValueKey.Comment
						) {
							return;
						}
						if (shouldSkipDocumentForGithub && entityValue === TypeFilterValueKey.Document) {
							return;
						}
						const shouldFilterByType =
							isVideoNounFilterEnabled(product) &&
							// entities.length > 0 ensures we only filter when specific types are selected;
							// when "All" is selected, entities is empty and we fall through to the else branch
							entities.length > 0;

						if (shouldFilterByType) {
							if (entities.includes(entityValue)) {
								productEntities = [...productEntities, ...entitiesConfig.entities3P];
							}
						} else {
							// When entities is empty (All selected), include all entities from the config
							productEntities = [...productEntities, ...entitiesConfig.entities3P];
						}
					});
					productEntities = [...new Set(productEntities)];
				}

				// Capture connectionId when present (dynamic Forge connectors)
				if (thirdPartyConfig.connectionId) {
					connectionIds = [thirdPartyConfig.connectionId];
				}
			} else {
				if (thirdPartyConfig.typeFilterConfig && isVideoNounFilterEnabled(product)) {
					// For Google Drive and SharePoint: only include entities from type filter configs that match selected entities
					// This ensures entity ATI is only included when the corresponding type is selected
					thirdPartyConfig.typeFilterConfig.forEach((entitiesConfig, entityValue) => {
						if (
							isCommentFilterSelected &&
							supportsEmbeddedComments &&
							isEmbeddedCommentsEnabledForProduct &&
							entityValue === TypeFilterValueKey.Comment
						) {
							return;
						}
						if (shouldSkipDocumentForGithub && entityValue === TypeFilterValueKey.Document) {
							return;
						}
						// Only include entities from type filter configs that match the selected entities
						if (entities.includes(entityValue)) {
							productEntities = [...productEntities, ...entitiesConfig.entities3P];
						}
					});
					productEntities = [...new Set(productEntities)];
				}
			}

			// If no specific typeFilterConfig matched, use default subtypes for the entities
			if (!hasMatchingTypeConfig && entities.length > 0) {
				entities.forEach((entity) => {
					const defaultSubtypes = DEFAULT_ENTITY_SUBTYPES[entity as TypeFilterValueKey] ?? [];
					if (defaultSubtypes.length > 0) {
						localSubtypes = [...localSubtypes, ...defaultSubtypes];
					}
				});
			}

			// Add local subtypes to the global subtypes array and remove duplicates
			allSubtypes = [...new Set([...allSubtypes, ...localSubtypes])];

			const integrationId = thirdPartyConfig.integrationAri;
			const providerId = thirdPartyConfig.providerId;
			const connectorSources = filterConnectorSources(
				product,
				isAllProductSearch,
				thirdPartyConfigs,
				thirdPartyConfig.connectorSources,
			);
			// Impersonation logging: surface, per connector source, which sources are served via
			// impersonation (user NOT OAuthed AND impersonation enabled for that source) so the
			// aggregator can observe impersonated request success/failure during rollout. Resolved
			// per connector source because a product can expose multiple sources where impersonation
			// is only enabled for some of them. Gated behind `weaklink-logging-fg`.
			const connectorSourcesImpersonationEnabled =
				isImpersonationEnabled(product, thirdPartyConfigs) && fg('weaklink-logging-fg')
					? getImpersonatedConnectorSources(thirdPartyConfig, connectorSources)
					: undefined;
			product = product === ProductKey.JsmDC ? ProductKey.JiraDC : product;

			// Only fan out per-datasource when a specific datasource is selected, OR when
			// getFiltersForSelectedDatasources affects thirdPartyProduct routing (e.g. Jira DC
			// sets a per-instance integrationId/providerId). For webcrawler, when "All" is
			// selected, all datasource entries would be identical (same integrationId, same
			// filters) — causing N duplicate requests to the search backend. Instead, fall
			// through to the single-entry else branch below.
			const shouldFanOut =
				thirdPartyConfig.datasources &&
				thirdPartyConfig.fanOutConnectorSources &&
				(!!selectedDatasource || product !== ProductKeys.AtlassianWebCrawler);
			if (shouldFanOut && thirdPartyConfig.datasources) {
				const datasourcesForQuery = selectedDatasource
					? thirdPartyConfig.datasources.filter((ds) => ds.datasourceId === selectedDatasource)
					: thirdPartyConfig.datasources;

				for (const datasourceConfig of datasourcesForQuery) {
					const datasourceContainerTypes = datasourceConfig.containerTypes || [];
					allContainerTypes = [...new Set([...allContainerTypes, ...datasourceContainerTypes])];

					const thirdPartyProductFilterForThisProduct = {
						integrationId: integrationId,
						subtypes: localSubtypes as string[],
						providerId,
						product: product as string,
						connectorSources,
						...(connectorSourcesImpersonationEnabled && {
							connectorSourcesImpersonationEnabled,
						}),
						...(productEntities.length > 0 && { entities: productEntities }),
						...(connectionIds && { connectionIds }),
						...(linkedEntityGranularity && { linkedEntityGranularity }),
					};
					// For webcrawler (flag ON), getFiltersForSelectedDatasources returns only { additionalTexts }
					// with no thirdPartyProduct key, so allFilters?.thirdPartyProduct is always undefined for
					// webcrawler — the else branch always runs and additionalTexts is discarded here.
					// The webcrawler's additionalTexts is correctly accumulated in the datasourceFilters loop below,
					// making this call a no-op for webcrawler. Only thirdPartyProduct is consumed in this code path.
					const allFilters =
						product !== ProductKeys.AtlassianWebCrawler ||
						fg('webcrawler-fanout-ui-sidebar-enabled')
							? thirdPartyConfig.getFiltersForSelectedDatasources?.(datasourceConfig, {})
							: undefined;
					if (allFilters?.thirdPartyProduct) {
						thirdPartyProducts.push({
							...thirdPartyProductFilterForThisProduct,
							...allFilters.thirdPartyProduct,
						});
					} else {
						thirdPartyProducts.push(thirdPartyProductFilterForThisProduct);
					}
				}
			} else {
				thirdPartyProducts.push({
					integrationId: integrationId,
					subtypes: localSubtypes as string[],
					providerId,
					product: product as string,
					connectorSources,
					...(connectorSourcesImpersonationEnabled && {
						connectorSourcesImpersonationEnabled,
					}),
					...(productEntities.length > 0 && { entities: productEntities }),
					...(connectionIds && { connectionIds }),
					...(linkedEntityGranularity && { linkedEntityGranularity }),
				});
			}
		}

		if (thirdPartyProducts) {
			// Add Loom and Bitbucket to the thirdPartyProducts filter
			const firstPartyProductsFor3pFilters: ProductKeys[] = [ProductKey.Loom, ProductKey.Bitbucket];

			const result = firstPartyProductsFor3pFilters
				.filter(
					(product) =>
						products.includes(product) && !thirdPartyProducts?.some((p) => p.product === product),
				)
				.map((product) => ({
					integrationId: productToIntegrationARIMap[product] || '',
					subtypes: allSubtypes as string[],
					product: product as string,
					connectorSources: [FULL_CONNECTOR_SOURCE] as ConnectorSource[],
				}));
			thirdPartyProducts = thirdPartyProducts.concat(result);
		}

		// Slack channels filter — Slack is a federated product, so it is excluded from the
		// `filteredProducts` loop above (federated products are filtered out). To pass selected
		// Slack channels to the aggregator, we attach them to a Slack `thirdPartyProducts` entry
		// here, either by mutating an existing entry (non-federated case) or by adding a new entry.
		if (slackChannels?.length) {
			const slackConfig = thirdPartyConfigs[ProductKey.Slack as ProductKeys3P];
			if (slackConfig) {
				const existingSlackEntry = thirdPartyProducts.find((p) => p.product === ProductKey.Slack);
				if (existingSlackEntry) {
					existingSlackEntry.channels = slackChannels;
				} else {
					thirdPartyProducts.push({
						integrationId: slackConfig.integrationAri || '',
						subtypes: [],
						providerId: slackConfig.providerId,
						product: ProductKey.Slack,
						connectorSources: filterConnectorSources(
							ProductKey.Slack,
							isAllProductSearch,
							thirdPartyConfigs,
							slackConfig.connectorSources || [],
						),
						channels: slackChannels,
					});
				}
			}
		}

		const createdByFilterField = { createdBy: createdBy || [] };
		const lastUpdatedByFilterField = { lastUpdatedBy: lastUpdatedBy || [] };

		// Labels filter is only applicable to Confluence (incl. Confluence DC).
		const isConfluenceProduct =
			products.includes(ProductKeys.Confluence) || products.includes(ProductKeys.ConfluenceDC);
		const labelsFilterField = isConfluenceProduct ? { labels: labels || [] } : {};

		const containerTypes = thirdPartyProducts.length !== 1 ? [] : allContainerTypes;

		const excludeSubtypes: string[] = [];

		const isTeamsProduct = products.includes(ProductKeys.Teams);
		const isAttachmentTypeSelected = entities.includes(TypeFilterValueKey.Attachment);
		const teamsFilters = buildTeamsFilters(
			isTeamsProduct && (isAttachmentTypeSelected || hasAttachment),
			isTeamsProduct ? workspaceName : undefined,
		)?.teamsFilters;

		let datasourceFilters: Partial<ThirdPartyFilters> = { additionalTexts: additionalTexts || [] };
		if (selectedDatasource) {
			for (const product of filteredProducts) {
				const thirdPartyConfig = thirdPartyConfigs[product as ProductKeys3P];
				if (
					!thirdPartyConfig?.getFiltersForSelectedDatasources ||
					!thirdPartyConfig.fanOutConnectorSources
				) {
					continue;
				}

				if (thirdPartyConfig.datasources) {
					const datasourceForQuery = thirdPartyConfig.datasources.find(
						(ds) => ds.datasourceId === selectedDatasource,
					);

					if (datasourceForQuery) {
						if (
							product !== ProductKeys.AtlassianWebCrawler ||
							fg('webcrawler-fanout-ui-sidebar-enabled')
						) {
							datasourceFilters =
								thirdPartyConfig.getFiltersForSelectedDatasources(
									datasourceForQuery,
									datasourceFilters,
								) ?? datasourceFilters;
						}
					}
				}
			}
		}

		const { additionalTexts: mergedAdditionalTexts = [], ...otherDatasourceFilters } =
			datasourceFilters;

		if (Object.keys(productToIntegrationARIMap).length !== 0) {
			const integrationARIs: string[] = products
				.filter(
					(product) =>
						// Non-3P products pass through
						(!is3pProductKey(product) && !isForgeConnectorProductKey(product)) ||
						// 3P products must be enabled and not require OAuth (or have impersonation)
						(thirdPartyConfigs[product as ProductKeys3P]?.isEnabled?.() !== false &&
							(!thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth ||
								isImpersonationEnabled(product, thirdPartyConfigs))),
				)
				.map((product) => productToIntegrationARIMap[product])
				.filter((ari): ari is string => !!ari)
				// remove duplicates (eg sharepoint, teams)
				.filter((ari, pos, arr) => {
					return arr.indexOf(ari) === pos;
				})
				// Include Slack integration ARI only when Slack has at least one connector source (after filtering)
				.filter(
					(ari) =>
						ari !== productToIntegrationARIMap[ProductKey.Slack] ||
						hasConnectorSources(ProductKey.Slack, isAllProductSearch, thirdPartyConfigs),
				);

			// Build salesforceFilters if applicable
			const salesforceIsClosedValue = opportunityStatus
				? OpportunityStatusFilterValue[opportunityStatus as OpportunityStatusFilterValueKey]
				: undefined;
			const salesforceFilters =
				salesforceOwners?.length ||
				salesforceStage?.length ||
				salesforceCloseDate ||
				opportunityStatus ||
				campaignStatuses?.length ||
				caseStatuses?.length
					? {
							...(salesforceOwners?.length && { owners: salesforceOwners }),
							...(salesforceStage?.length && { stage: salesforceStage }),
							...(salesforceCloseDate ? { closeDate: salesforceCloseDate } : {}),
							...(salesforceIsClosedValue !== undefined
								? { isClosed: salesforceIsClosedValue }
								: {}),
							...(campaignStatuses?.length && { campaignStatuses }),
							...(caseStatuses?.length && { caseStatuses }),
						}
					: undefined;

			return {
				range,
				titleMatchOnly: titleMatchValue,
				subtypes: allSubtypes,
				integrations: integrationARIs,
				thirdPartyProducts,
				assignees,
				containerAris: containerAris || [],
				...createdByFilterField,
				...lastUpdatedByFilterField,
				ancestorAris: ancestorAris || [],
				...labelsFilterField,
				additionalTexts:
					mergedAdditionalTexts.length > 0 ? mergedAdditionalTexts : additionalTexts || [],
				containerTypes: containerTypes,
				...(excludeSubtypes.length > 0 && { excludeSubtypes }),
				...(salesforceFilters && { salesforceFilters }),
				...buildGitFilters(pullRequestStatus, repoName, reviewer),
				...(teamsFilters && { teamsFilters }),
				...otherDatasourceFilters,
			};
		} else {
			// Build salesforceFilters if applicable
			const salesforceIsClosedValue = opportunityStatus
				? OpportunityStatusFilterValue[opportunityStatus as OpportunityStatusFilterValueKey]
				: undefined;
			const salesforceFilters =
				salesforceOwners?.length ||
				salesforceStage?.length ||
				salesforceCloseDate ||
				opportunityStatus ||
				campaignStatuses?.length ||
				caseStatuses?.length
					? {
							...(salesforceOwners?.length && { owners: salesforceOwners }),
							...(salesforceStage?.length && { stage: salesforceStage }),
							...(salesforceCloseDate ? { closeDate: salesforceCloseDate } : {}),
							...(salesforceIsClosedValue !== undefined
								? { isClosed: salesforceIsClosedValue }
								: {}),
							...(campaignStatuses?.length && { campaignStatuses }),
							...(caseStatuses?.length && { caseStatuses }),
						}
					: undefined;

			return {
				range,
				titleMatchOnly: titleMatchValue,
				subtypes: allSubtypes,
				integrations: [],
				thirdPartyProducts,
				assignees,
				containerAris: containerAris || [],
				...createdByFilterField,
				...lastUpdatedByFilterField,
				ancestorAris: ancestorAris || [],
				...labelsFilterField,
				additionalTexts:
					mergedAdditionalTexts.length > 0 ? mergedAdditionalTexts : additionalTexts || [],
				containerTypes: containerTypes,
				...(excludeSubtypes.length > 0 && { excludeSubtypes }),
				...(salesforceFilters && { salesforceFilters }),
				...buildGitFilters(pullRequestStatus, repoName, reviewer),
				...(teamsFilters && { teamsFilters }),
				...otherDatasourceFilters,
			};
		}
	}

	return {
		titleMatchOnly: false,
		subtypes: [],
		integrations: [],
	};
};

export enum DateRangeError {
	FROM_AFTER_TO_ERROR = 'fromAfterToError',
	FROM_IN_FUTURE_ERROR = 'fromInFutureError',
	TO_IN_FUTURE_ERROR = 'toInFutureError',
}

export const getInvalidDateRange: (from: string, to: string) => DateRangeError | null = (
	from: string,
	to: string,
): DateRangeError | null => {
	if (!from && !to) {
		return null;
	}
	const dateFrom: Date = new Date(`${from}${from ? 'T00:00:00' : ''}`);
	const dateTo: Date = new Date(`${to}${to ? 'T00:00:00' : ''}`);
	const now: Date = new Date();

	if (dateFrom > now) {
		return DateRangeError.FROM_IN_FUTURE_ERROR;
	}

	if (dateFrom > dateTo) {
		return DateRangeError.FROM_AFTER_TO_ERROR;
	}

	if (dateTo > now) {
		return DateRangeError.TO_IN_FUTURE_ERROR;
	}

	return null;
};

export const getProducts3PFromConfigForFilter: (filterId: FilterId) => ProductKeys3P[] =
	_getProducts3PFromConfigForFilter;

export const notEmpty: <TValue>(value: TValue | null | undefined) => value is TValue = <TValue>(
	value: TValue | null | undefined,
): value is TValue => {
	return value !== null && value !== undefined;
};

export const isImpersonationEnabled = (
	product: string,
	thirdPartyConfigs: Partial<Record<ProductKeys3P, ThirdPartyConfigsWithAPIResponse>>,
): boolean => {
	const impersonationEnabled = thirdPartyConfigs[product as ProductKeys3P]?.impersonationEnabled;

	return !!(
		impersonationEnabled &&
		((product === ProductKey.GoogleDrive && serveWeakLinkExpWithoutExposure()) ||
			(product === ProductKey.Github && fg('weaklink-github-fg')))
	);
};

/**
 * Derives the single connector source backing a datasource config, mirroring the resolution used
 * when building the product's `connectorSources` list (smartlinks / lite / federated / full).
 */
const getDatasourceConnectorSource = (
	datasource: ThirdPartyConfigsWithAPIResponse,
): ConnectorSource | undefined => {
	const resourceType = datasource.resourceType;
	// Without a resourceType the backing connector source cannot be determined, so contribute
	// nothing rather than guessing. This mirrors rovo-connectors-config's `getConnectorSources`,
	// which early-returns `[]` for this case, keeping both FPS/QF and agent-studio builders in sync.
	if (!resourceType) {
		return undefined;
	}
	if (resourceType.startsWith(SMARTLINKS_RESOURCE_TYPE_PREFIX)) {
		return SMARTLINKS_CONNECTOR_SOURCE;
	}
	if (resourceType.startsWith(LITE_CONNECTOR_SOURCE_PREFIX)) {
		return LITE_CONNECTOR_SOURCE;
	}
	// Slack federated status is driven by `connectorSyncType` rather than the static `isFederated`
	// flag. Mirror the canonical derivation in controllers/config/utils/third-party-products.ts so a
	// federated Slack datasource resolves to FEDERATED (not FULL). `connectorSyncType` is present on
	// the datasource at runtime (spread from the raw resource) but not on the typed config shape.
	if (
		datasource.id === ProductKeys.Slack &&
		fg('ai_3p_slack_connector_status_transition_enabled')
	) {
		const connectorSyncType = (datasource as { connectorSyncType?: string | null })
			.connectorSyncType;
		return connectorSyncType === 'federated' ? FEDERATED_CONNECTOR_SOURCE : FULL_CONNECTOR_SOURCE;
	}
	if (datasource.isFederated) {
		return FEDERATED_CONNECTOR_SOURCE;
	}
	return FULL_CONNECTOR_SOURCE;
};

/**
 * Returns the subset of `connectorSources` that are served via impersonation for this product.
 *
 * Impersonation is resolved per connector source (each backing datasource maps to a single
 * connector source) rather than at the product level, because a product can expose multiple
 * connector sources (e.g. `smartlinks` + `full-connector`) where impersonation is only enabled
 * for some of them. A connector source is considered impersonated when its datasource has the
 * user NOT OAuthed (`userNeedsOAuth === true`) AND impersonation enabled (`impersonationEnabled`).
 *
 * The result is restricted to sources actually present in the product's `connectorSources` and is
 * deduped while preserving the order of `connectorSources`. Returns `undefined` when no connector
 * source is impersonated.
 */
const getImpersonatedConnectorSources = (
	thirdPartyConfig: ThirdPartyConfigsWithAPIResponse,
	connectorSources: readonly ConnectorSource[] | undefined,
): ConnectorSource[] | undefined => {
	if (!connectorSources?.length) {
		return undefined;
	}

	const impersonatedSources = new Set<ConnectorSource>();
	const datasources = thirdPartyConfig.datasources?.length
		? thirdPartyConfig.datasources
		: [thirdPartyConfig];
	for (const datasource of datasources) {
		if (datasource.userNeedsOAuth && datasource.impersonationEnabled) {
			const source = getDatasourceConnectorSource(datasource);
			if (source) {
				impersonatedSources.add(source);
			}
		}
	}

	if (!impersonatedSources.size) {
		return undefined;
	}

	const result = connectorSources.filter((source) => impersonatedSources.has(source));
	return result.length ? result : undefined;
};
